// src/templates/family-faq.js - FAQ page with i18n support and centralized theming
import React, { useState, useEffect } from "react"
import { graphql, Link } from "gatsby"
import { useTranslation } from "gatsby-plugin-react-i18next"
import SEO from "../components/seo"
import AdoptionHeader from "../components/AdoptionHeader"
import AdoptionFooter from "../components/AdoptionFooter"

const FamilyFAQ = ({ data, pageContext }) => {
  const { t } = useTranslation()
  const family = data.account
  const { familyWebsite } = pageContext
  const [openFAQ, setOpenFAQ] = useState(null)

  if (!family) {
    return (
      <div>
        <AdoptionHeader />
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <div className="text-center">
            <h1 className="mb-4 text-4xl font-bold text-gray-700 font-heading-main">
              {t('common.familyNotFound')}
            </h1>
            <p className="text-lg text-gray-700 font-body">
              {t('common.familyNotFoundMessage')}
            </p>
          </div>
        </div>
        <AdoptionFooter />
      </div>
    )
  }

  const familyName = family.name ? family.name.replace(' / ', ' and ') : "Adoptive Family"
  const displayName = familyName.replace(' Family', '').replace('The ', '')
  
  // Translation helpers for family type
  const familyTypeText = family.account_type === 'Couple' ? t('common.us') : t('common.me')

  // Generate comprehensive SEO data
  const pageTitle = t('faqPage.seoTitle', { familyName })
  const pageDescription = t('faqPage.seoDescription')
  
  const keywords = [
    t('faqPage.seoKeywords.adoptionFAQ'),
    t('faqPage.seoKeywords.adoptionQuestions'),
    t('faqPage.seoKeywords.birthParentRights'),
    t('faqPage.seoKeywords.adoptionCosts'),
    t('faqPage.seoKeywords.adoptionSupport'),
    family.contactInfo?.state?.name && t('faqPage.seoKeywords.stateAdoption', { state: family.contactInfo.state.name }),
    t('faqPage.seoKeywords.adoptionProcess')
  ].filter(Boolean)

  // FAQ questions from translations
  const faqQuestions = t('faq.questions', { returnObjects: true })

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index)
  }

  return (
    <div className="font-sans bg-white" style={{ margin: 0, padding: 0 }}>
      <SEO
        title={pageTitle}
        description={pageDescription}
        pathname="/faq"
        keywords={keywords}
        familyData={family}
        image={family.avatar ? `/images/families/${family.avatar}` : null}
      />
      
      <AdoptionHeader family={family} agencyId={family.agency?.mongoId} accountId={family.mongoId} />

      <main>
        {/* Page Header */}
        <div className="py-16 lg:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-gray-700 text-4xl lg:text-5xl mb-8 font-heading-main">
              {t('faq.title')}
            </h1>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="pb-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-4">
              {faqQuestions.map((faq, index) => (
                <div key={index}>
                  
                  {/* Question Box */}
                  <div 
                    className={`rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:shadow-sm ${
                      openFAQ === index ? 'bg-accent' : 'bg-gray-50'
                    }`}
                    onClick={() => toggleFAQ(index)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className={`font-medium pr-4 font-body ${
                        openFAQ === index ? 'text-dark' : 'text-gray-700'
                      }`}>
                        {faq.question}
                      </h3>
                      <div className="flex-shrink-0">
                        <span
                          className="material-icons transition-transform duration-300 text-dark"
                          style={{ 
                            fontSize: '30px', 
                            transform: openFAQ === index ? 'rotate(45deg)' : 'rotate(0deg)',
                            filter: openFAQ === index ? 'brightness(0) invert(0.3)' : 'none' 
                          }}>
                            add
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Answer Section */}
                  {openFAQ === index && (
                    <div className="px-6 py-6 bg-gray-50 rounded-b-2xl mt-1">
                      <p className="text-gray-700 leading-relaxed font-body">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl mb-6 text-gray-500 font-heading-main">
              {t('faqPage.stillHaveQuestions')}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-3xl mx-auto font-body">
              {t('faqPage.stillHaveQuestionsDescription')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="btn-primary"
              >
                {t('faqPage.contactToday', { familyTypeText: familyTypeText.toUpperCase() })}
              </Link>
              <Link 
                to="/about"
                className="btn-primary"
              >
                {t('faqPage.learnAbout', { familyTypeText: familyTypeText.toUpperCase() })}
              </Link>
            </div>
          </div>
        </div>

      </main>

      <AdoptionFooter family={family} />
    </div>
  )
}

export const Head = ({ data }) => (
  <SEO title={`FAQ - ${data.account?.name || 'Our Family'} | Adoption Questions & Answers`} />
)

export const query = graphql`
  query($accountId: String!, $language: String!) {
    account(id: { eq: $accountId }) {
      id
      mongoId
      name
      status
      account_type
      waiting_for
      contactInfo {
        website
        state {
          name
        }
      }
      avatar
      banner_photo
      agency {
        mongoId
        name
        address {
          line_1
          line_2
          street
          city
          state {
            name
          }
          zip
        }
        email {
          email
        }
        phone {
          number
        }
        bm_contact {
          email
          phone
          text
        }
        website
        avatar
      }
    }
    locales: allLocale(filter: {language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`

export default FamilyFAQ